import { formatDate } from "@/features/FormCard/utils";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { useQueryCache } from "@/hooks/useQueyCache";
import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Divider } from "@heroui/divider";
import { Input } from "@heroui/input";
import { Link } from "@heroui/link";
import { useTranslation } from "react-i18next";

type TInfoCardProps = {
  id: number;
  table_name: string;
  client: string;
  include_tsf_id?: boolean;
  align?: "center" | "start";
};

interface ItemData {
  id: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export type TechnicalInfoData = {
  time_serie_file_id?: number | string | null;
  [key: string]: number | string | null | undefined;
};

export type TechnicalInfoFileReference = {
  fileId: string;
  href: string | null;
};

export function getClientSpecificTimeSerieFileKey(client: string) {
  return `${client}_time_serie_file_id`;
}

export function resolveTechnicalInfoFileReference(
  data: TechnicalInfoData | undefined,
  client: string,
): TechnicalInfoFileReference {
  const clientSpecificKey = getClientSpecificTimeSerieFileKey(client);
  const clientSpecificId = data?.[clientSpecificKey];

  if (clientSpecificId !== null && clientSpecificId !== undefined) {
    const encodedClient = encodeURIComponent(client);
    const encodedFileId = encodeURIComponent(String(clientSpecificId));
    return {
      fileId: String(clientSpecificId),
      href: `/admin/${encodedClient}/${encodedClient}_data_upload/${encodedFileId}`,
    };
  }

  const genericId = data?.time_serie_file_id;

  if (genericId !== null && genericId !== undefined) {
    const encodedWorkspace = encodeURIComponent("qtl - etl");
    const encodedFileId = encodeURIComponent(String(genericId));
    return {
      fileId: String(genericId),
      href: `/admin/${encodedWorkspace}/time_serie_files/${encodedFileId}`,
    };
  }

  return {
    fileId: "",
    href: null,
  };
}

export function TechnicalInfoCard({
  id,
  table_name,
  client,
  include_tsf_id = true,
  align = "center",
}: TInfoCardProps) {
  const { t } = useTranslation();

  const data = useQueryCache<ItemData>([client, table_name, "get", "item", id]);
  const { fileId, href } = resolveTechnicalInfoFileReference(data, client);

  return (
    <div
      className={`bg-gray-50 h-full max-w-card w-full ${align === "center" ? "grid place-items-center" : ""}`}
    >
      <div
        className={`w-full ${align === "center" ? "flex justify-center" : ""}`}
      >
        <Card className="p-4 bg-white w-full">
          <CardHeader className="text-sm">
            {t("technical_info.title")}
          </CardHeader>

          <Divider />

          <CardBody>
            <div className="grid grid-cols-2 lg:flex lg:flex-row py-2 gap-3">
              <Input
                radius="full"
                variant="underlined"
                labelPlacement="outside"
                size="sm"
                type="text"
                label={t("technical_info.id")}
                value={id.toString()}
                readOnly
              />

              {include_tsf_id && (
                <Input
                  radius="full"
                  variant="underlined"
                  labelPlacement="outside"
                  size="sm"
                  type="text"
                  label={t("technical_info.file_id")}
                  value={fileId || " "}
                  readOnly
                  endContent={
                    href ? (
                      <Button
                        href={toWorkspacePath(href)}
                        variant="light"
                        color="primary"
                        showAnchorIcon
                        as={Link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-transparent shadow-none p-0 min-w-0 h-6 w-6"
                      />
                    ) : null
                  }
                />
              )}

              <Input
                radius="full"
                variant="underlined"
                labelPlacement="outside"
                size="sm"
                type="text"
                label={t("technical_info.created_at")}
                value={formatDate(data?.created_at || "")}
                readOnly
              />

              <Input
                radius="full"
                variant="underlined"
                labelPlacement="outside"
                size="sm"
                type="text"
                label={t("technical_info.updated_at")}
                value={formatDate(data?.updated_at || "")}
                readOnly
              />
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
